import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, ArrowLeft, Chrome } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth-context';
import { loginSchema, type LoginData } from '@shared/schema';
import { trackEvent } from '@/lib/analytics/posthog';

export default function LoginPage() {
  const [, setLocation] = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const { login, loginWithGoogle } = useAuth();

  const form = useForm<LoginData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginData) => {
    try {
      await login(data.email, data.password);
      try {
        trackEvent('login_completed', { method: 'email' });
      } catch {}
      toast({
        title: 'Welcome back!',
        description: 'You have been logged in successfully.',
      });
      setLocation('/');
    } catch (error) {
      try {
        trackEvent('login_failed', {
          method: 'email',
          error_message: error instanceof Error ? error.message : 'unknown',
        });
      } catch {}
      toast({
        variant: 'destructive',
        title: 'Login failed',
        description: error instanceof Error ? error.message : 'Please check your credentials and try again.',
      });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
      try {
        trackEvent('login_google_initiated', { method: 'google' });
      } catch {}
      // Note: User will be redirected to Google, then back to /auth/callback
      // The callback page will handle the final redirect
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Google login failed',
        description: error instanceof Error ? error.message : 'Unable to connect with Google. Please try again.',
      });
    }
  };

  return (
    <div className="bg-black text-white relative min-h-screen overflow-hidden">
      {/* subtle grayscale background accents replacing colorful orbs */}
      <div className="floating-orb absolute z-0 w-96 h-96 rounded-full blur-3xl -top-48 -left-48 animate-float-slow" />
      <div className="floating-orb absolute z-0 w-80 h-80 rounded-full blur-3xl top-1/3 -right-40 animate-float-reverse" />
      <div className="floating-orb absolute z-0 w-64 h-64 rounded-full blur-3xl bottom-1/4 left-1/4 animate-float-slow" />

      {/* Back button pinned to viewport */}
      <Link href="/">
        <Button
          variant="ghost"
          className="absolute left-4 top-4 z-20 text-white/80 hover:text-white hover:bg-white/10 backdrop-blur-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>
      </Link>

      <div className="relative z-10 min-h-screen grid place-items-center p-4 sm:p-6 pt-16 sm:pt-0">
        <div className="w-full max-w-md mx-auto">
          {/* moved Back button to top-left absolute */}

          <Card className="relative glass-effect floating-modal soft-glow gradient-outline border-white/10 shadow-2xl">
            {/* thin accent bar on top */}
            <div className="pointer-events-none absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-apple-gray via-white to-apple-gray opacity-70 rounded-t-xl" />
            <CardHeader className="text-center pb-4 sm:pb-6">
              <CardTitle className="text-xl sm:text-2xl font-bold text-white">
                Welcome back
              </CardTitle>
              {/* Removed subtitle per request: "Sign in to your Bounce Bunny account" */}
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="your@email.com"
                            type="email"
                            {...field}
                            className="glass-input"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              placeholder="Enter your password"
                              type={showPassword ? 'text' : 'password'}
                              {...field}
                              className="glass-input pr-10"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 text-white/60 hover:text-white hover:bg-transparent"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex justify-end">
                    <Link href="/forgot-password">
                      <Button
                        variant="link"
                        className="text-sm text-white/70 hover:text-white p-0 h-auto no-underline hover:no-underline focus:no-underline"
                      >
                        Forgot password?
                      </Button>
                    </Link>
                  </div>

                  <Button
                    type="submit"
                    variant="apple"
                    className="w-full apple-lift-1"
                    disabled={form.formState.isSubmitting}
                  >
                    {form.formState.isSubmitting ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </Form>

              <div className="relative">
                <div className="flex items-center">
                  <div className="w-full border-t border-white/20" />
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full glass-effect border-white/20 text-white hover:bg-white/10"
                onClick={handleGoogleLogin}
              >
                <Chrome className="w-4 h-4 mr-2" />
                Sign in with Google
              </Button>

              <div className="text-center space-y-3">
                <span className="text-white/60">Don't have an account? </span>
                <Button
                  asChild
                  variant="outline"
                  className="w-full glass-effect border-white/20 text-white hover:bg-white/10"
                >
                  <Link href="/signup">Create Account In 3 seconds</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}